import React from "react";
import {
  Stack,
  Card,
  Text,
  Group,
  Badge,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconCalendarEvent,
  IconX,
  IconClock,
  IconCircleCheckFilled,
  IconCircle,
} from "@tabler/icons-react";
import { useMediaQuery } from "@mantine/hooks";
import moment from "moment";
import {
  SelectedService,
  ServiceSchedule,
} from "../../../../../../types/availability";
import { getServiceTotalDuration } from "./schedulingCalendarUtils";
import styles from "./ServiceScheduleList.module.css";

interface ServiceScheduleListProps {
  services: SelectedService[];
  schedules: Record<string, ServiceSchedule>;
  activeServiceId: string | null;
  onSelectActive: (serviceId: string) => void;
  onClearSchedule: (serviceId: string) => void;
}

const formatScheduledDateTime = (schedule: ServiceSchedule): string =>
  moment(`${schedule.date} ${schedule.time}`, "YYYY-MM-DD HH:mm").format(
    "ddd, MMM D [at] h:mm A",
  );

export default function ServiceScheduleList({
  services,
  schedules,
  activeServiceId,
  onSelectActive,
  onClearSchedule,
}: ServiceScheduleListProps) {
  const scheduledCount = services.filter((s) => !!schedules[s.id]).length;

  // On tablet/mobile the panes stack vertically (see ScheduleServicesStep's
  // Flex `md` breakpoint), so compact the header away and put each service's
  // name + staff on a single line to save vertical space.
  const isStacked = useMediaQuery("(max-width: 61.99em)");

  return (
    <Stack gap="sm" p="xs">
      {!isStacked && (
        <Text fw={600} size="sm">
          Services to schedule
        </Text>
      )}
      <Text size="xs" c="dimmed" mt={isStacked ? 0 : -8}>
        Select a service and pick a time on the calendar
      </Text>

      {services.map((service) => {
        const schedule = schedules[service.id];
        const isActive = service.id === activeServiceId;

        return (
          <Card
            key={service.id}
            withBorder
            radius="md"
            padding="md"
            className={`${styles.card} ${isActive ? styles.cardActive : ""}`}
            role="button"
            tabIndex={0}
            aria-current={isActive || undefined}
            aria-label={`${service.serviceName} with ${service.staffName}, ${
              schedule ? "scheduled" : "not scheduled"
            }`}
            onClick={() => onSelectActive(service.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelectActive(service.id);
              }
            }}
          >
            <Group
              wrap="nowrap"
              align="center"
              gap="sm"
              style={{ minWidth: 0 }}
            >
              {/* Status indicator — green check when scheduled, thin gray hollow
                  circle when pending. */}
              {schedule ? (
                <IconCircleCheckFilled
                  size={34}
                  style={{
                    color: "var(--mantine-color-green-6)",
                    flexShrink: 0,
                  }}
                  aria-label={`${service.serviceName} scheduled`}
                />
              ) : (
                <IconCircle
                  size={34}
                  stroke={1.5}
                  style={{
                    color: "var(--mantine-color-gray-4)",
                    flexShrink: 0,
                  }}
                  aria-hidden
                />
              )}

              <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                {isStacked ? (
                  <Text size="sm" lineClamp={1}>
                    <Text span fw={600} inherit>
                      {service.serviceName}
                    </Text>
                    <Text span c="dimmed" inherit>
                      {" "}
                      with {service.staffName}
                    </Text>
                  </Text>
                ) : (
                  <>
                    <Text fw={600} size="sm" lineClamp={1}>
                      {service.serviceName}
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      with {service.staffName}
                    </Text>
                  </>
                )}
                <Group gap={6} wrap="nowrap">
                  <IconClock
                    size={14}
                    style={{ color: "var(--mantine-color-gray-6)" }}
                  />
                  <Text size="xs" c="dimmed">
                    {getServiceTotalDuration(service)} min • $
                    {service.price.toFixed(2)}
                  </Text>
                </Group>
                {schedule ? (
                  <Badge
                    variant="light"
                    color="var(--brand-teal)"
                    leftSection={<IconCalendarEvent size={12} />}
                    styles={{ label: { textTransform: "none" } }}
                  >
                    {formatScheduledDateTime(schedule)}
                  </Badge>
                ) : (
                  <Text size="xs" fs="italic" c="dimmed">
                    Not scheduled
                  </Text>
                )}
              </Stack>

              {schedule && (
                <Tooltip label="Clear time" withArrow position="left">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label={`Clear scheduled time for ${service.serviceName}`}
                    style={{ flexShrink: 0, alignSelf: "flex-start" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onClearSchedule(service.id);
                    }}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          </Card>
        );
      })}

      <Text size="xs" c="dimmed" ta="center" mt={4}>
        {scheduledCount} of {services.length} service
        {services.length === 1 ? "" : "s"} tentatively scheduled
      </Text>
    </Stack>
  );
}
